interface Point {
    x: number;
    y: number;
}

interface Intersection {
    point: Point;
    segment1Index: number;
    segment2Index: number;
}

export class PolygonLoopSeparatorService {
    // Check if two line segments intersect
    private doSegmentsIntersect(
        p1: Point, p2: Point,
        p3: Point, p4: Point
    ): Point | null {
        const denominator = (p4.y - p3.y) * (p2.x - p1.x) - (p4.x - p3.x) * (p2.y - p1.y);
        
        if (denominator === 0) return null;
        
        const ua = ((p4.x - p3.x) * (p1.y - p3.y) - (p4.y - p3.y) * (p1.x - p3.x)) / denominator;
        const ub = ((p2.x - p1.x) * (p1.y - p3.y) - (p2.y - p1.y) * (p1.x - p3.x)) / denominator;
        
        if (ua < 0 || ua > 1 || ub < 0 || ub > 1) return null;
        
        return {
            x: p1.x + ua * (p2.x - p1.x),
            y: p1.y + ua * (p2.y - p1.y)
        };
    }

    // Find all intersections in the polygon
    private findIntersections(points: Point[]): Intersection[] {
        const intersections: Intersection[] = [];
        
        for (let i = 0; i < points.length - 1; i++) {
            for (let j = i + 2; j < points.length - 1; j++) {
                const intersection = this.doSegmentsIntersect(
                    points[i], points[i + 1],
                    points[j], points[j + 1]
                );
                
                if (intersection) {
                    intersections.push({
                        point: intersection,
                        segment1Index: i,
                        segment2Index: j
                    });
                }
            }
        }
        
        return intersections;
    }

    // Calculate polygon area
    private calculatePolygonArea(points: Point[]): number {
        let area = 0;
        for (let i = 0; i < points.length; i++) {
            const j = (i + 1) % points.length;
            area += points[i].x * points[j].y;
            area -= points[j].x * points[i].y;
        }
        return Math.abs(area) / 2;
    }

    // Check if a loop is valid (no self-intersections)
    private isValidLoop(points: Point[]): boolean {
        const intersections = this.findIntersections(points);
        return intersections.length === 0;
    }

    // Find all valid loops in the polygon
    private findAllValidLoops(points: Point[], intersections: Intersection[]): Point[][] {
        const validLoops: Point[][] = [];
        const visited = new Set<string>();

        // Helper function to check if a point is close to another point
        const arePointsClose = (p1: Point, p2: Point): boolean => {
            const threshold = 0.000001; // Adjust this threshold as needed
            return Math.abs(p1.x - p2.x) < threshold && Math.abs(p1.y - p2.y) < threshold;
        };

        // Helper function to get a unique key for a point
        const getPointKey = (point: Point): string => `${point.x},${point.y}`;

        // Helper function to find next points from current point
        const findNextPoints = (currentIndex: number): { point: Point; index: number }[] => {
            const nextPoints: { point: Point; index: number }[] = [];

            // Add regular next point
            if (currentIndex < points.length - 1) {
                nextPoints.push({
                    point: points[currentIndex + 1],
                    index: currentIndex + 1
                });
            }

            // Add intersection points
            intersections.forEach(int => {
                if (int.segment1Index === currentIndex) {
                    nextPoints.push({
                        point: int.point,
                        index: int.segment2Index
                    });
                } else if (int.segment2Index === currentIndex) {
                    nextPoints.push({
                        point: int.point,
                        index: int.segment1Index
                    });
                }
            });

            return nextPoints;
        };

        // Recursive function to build loops
        const buildLoop = (
            startPoint: Point,
            currentPoint: Point,
            currentIndex: number,
            currentLoop: Point[]
        ) => {
            currentLoop.push(currentPoint);
            const currentKey = getPointKey(currentPoint);

            // Check if we've completed a loop
            if (currentLoop.length > 2 && arePointsClose(currentPoint, startPoint)) {
                const loopKey = currentLoop.map(getPointKey).join('|');
                if (!visited.has(loopKey) && this.isValidLoop(currentLoop)) {
                    visited.add(loopKey);
                    validLoops.push([...currentLoop]);
                }
                return;
            }

            // Find and follow all possible next points
            const nextPoints = findNextPoints(currentIndex);
            nextPoints.forEach(({ point, index }) => {
                if (!visited.has(`${currentKey}|${getPointKey(point)}`)) {
                    visited.add(`${currentKey}|${getPointKey(point)}`);
                    buildLoop(startPoint, point, index, [...currentLoop]);
                }
            });
        };

        // Start building loops from each point
        points.forEach((point, index) => {
            buildLoop(point, point, index, []);
        });

        return validLoops;
    }

    // Main function to process the polygon
    public separateLoops(points: Point[]): {
        allLoops: Point[][];
        largestLoop: Point[];
    } {
        const intersections = this.findIntersections(points);
        
        if (intersections.length === 0) {
            return {
                allLoops: [points],
                largestLoop: points
            };
        }

        const validLoops = this.findAllValidLoops(points, intersections);
        
        // Sort loops by area (largest first)
        const sortedLoops = validLoops.sort((a, b) => 
            this.calculatePolygonArea(b) - this.calculatePolygonArea(a)
        );

        return {
            allLoops: sortedLoops,
            largestLoop: sortedLoops[0] || points
        };
    }
}
